import { formatDistanceToNow, parseISO } from 'date-fns';
import { Check, Lock, Sparkles } from 'lucide-react';
import { SatDomain, SatDomainMapEntryDto, DOMAIN_LABELS } from '@/api/sat';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { buildCoursePath, isPathPersonalized } from './coursePath';
import {
  SHORT_DOMAIN_LABELS,
  STUDENT_BAND_BAR_CLASSES,
  STUDENT_BAND_LABELS,
} from './studentCopy';
import { StopRing } from './StopRing';
import { SatCoursePathPreview } from './SatCoursePathPreview';

/**
 * "Your course path" — the personalized course as a flowing, winding trail.
 *
 * The SELL: this isn't a category grid, it's THEIR course. Stop order is
 * computed from the student's own evidence (weakest next), and the subtitle
 * says so out loud. The ribbon suggests the journey — solid along conquered
 * ground, dashed ahead — while each stop carries its OWN state (mastered ✓,
 * you-are-here pulse, per-stop score ring), so several domains in flight at
 * once read naturally. The trail is a recommendation, not a cage.
 *
 * On mount the map "unfolds": the ribbon draws itself and stops pop in,
 * staggered. Respects prefers-reduced-motion.
 */

interface SatCoursePathProps {
  domains: SatDomainMapEntryDto[];
  activeDomain: SatDomain | null;
  focusDomain: SatDomain | null;
  onFocusDomain: (domain: SatDomain | null) => void;
  initialDiagnosticCompletedAt: string | null;
  firstName: string | null;
  /**
   * `trail` (default): the personalized winding path. `preview`: the compact
   * first-run promise — eight empty rings, no ribbon — shown before there is
   * anything personal to lay out.
   */
  variant?: 'trail' | 'preview';
  /** Preview only: fired when the "See it fill in" beat finishes draining. */
  onFillEnd?: () => void;
}

/** Gentle wave of x-positions (%) for the stops — the "flow". */
const WAVE_X = [26, 62, 80, 52, 22, 50, 78, 44];
const ROW_H = 102;

function formatRelative(iso: string | null): string | null {
  if (!iso) return null;
  try {
    return formatDistanceToNow(parseISO(iso), { addSuffix: true });
  } catch {
    return null;
  }
}

export function SatCoursePath({
  domains,
  activeDomain,
  focusDomain,
  onFocusDomain,
  initialDiagnosticCompletedAt,
  firstName,
  variant = 'trail',
  onFillEnd,
}: SatCoursePathProps) {
  if (variant === 'preview') return <SatCoursePathPreview domains={domains} onFillEnd={onFillEnd} />;
  const personalized = isPathPersonalized(domains, initialDiagnosticCompletedAt);
  const path = buildCoursePath(domains, activeDomain);
  const diagnosticAgo = formatRelative(initialDiagnosticCompletedAt);
  const totalH = path.length * ROW_H;

  const pts = path.map((_, i) => ({ x: WAVE_X[i % WAVE_X.length], y: i * ROW_H + 38 }));
  const seg = (from: { x: number; y: number }, to: { x: number; y: number }) => {
    const midY = (from.y + to.y) / 2;
    return `C ${from.x} ${midY}, ${to.x} ${midY}, ${to.x} ${to.y}`;
  };
  const ribbonThrough = (points: typeof pts) =>
    points.map((p, i) => (i === 0 ? `M ${p.x} ${p.y}` : seg(points[i - 1], p))).join(' ');

  // Conquered ground: through every DONE stop and the CURRENT one.
  const lastSolidIdx = path.reduce(
    (acc, n, i) => (n.status === 'DONE' || n.status === 'CURRENT' ? i : acc),
    -1,
  );
  const solidRibbon = lastSolidIdx >= 1 ? ribbonThrough(pts.slice(0, lastSolidIdx + 1)) : null;
  const aheadRibbon =
    lastSolidIdx < pts.length - 1
      ? ribbonThrough(pts.slice(Math.max(lastSolidIdx, 0)))
      : null;

  return (
    <section className="space-y-3" data-testid="course-path">
      {/* Unfold animation. Scoped keyframes; motion-reduced users get the final state. */}
      <style>{`
        @keyframes sat-ribbon-draw { from { stroke-dashoffset: 1; } to { stroke-dashoffset: 0; } }
        @keyframes sat-ribbon-fade { from { opacity: 0; } to { opacity: 1; } }
        @keyframes sat-node-pop {
          from { opacity: 0; transform: translate(-50%, 10px) scale(0.8); }
          to   { opacity: 1; transform: translate(-50%, 0) scale(1); }
        }
        @keyframes sat-ribbon-flow { to { stroke-dashoffset: -9.5; } }
        @keyframes sat-ribbon-crawl { to { stroke-dashoffset: -10; } }
        .sat-ribbon-solid { stroke-dasharray: 1; stroke-dashoffset: 1; animation: sat-ribbon-draw 1.1s ease-out 0.15s both; }
        .sat-ribbon-energy { animation: sat-ribbon-fade 0.6s ease-out 1.3s both, sat-ribbon-flow 2.4s linear 1.3s infinite; }
        .sat-ribbon-ahead { animation: sat-ribbon-fade 0.8s ease-out 0.9s both, sat-ribbon-crawl 7s linear 1.7s infinite; }
        .sat-node { animation: sat-node-pop 0.5s cubic-bezier(0.2, 0.9, 0.3, 1.15) both; }
        @media (prefers-reduced-motion: reduce) {
          .sat-ribbon-solid, .sat-ribbon-energy, .sat-ribbon-ahead, .sat-node { animation: none !important; opacity: 1; stroke-dashoffset: 0; }
        }
      `}</style>

      <div>
        <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          Your course path
        </h3>
        <p className="text-xs text-muted-foreground" data-testid="course-path-subtitle">
          {personalized ? (
            <>
              <Sparkles className="mr-1 inline h-3 w-3 text-primary" aria-hidden />
              Built {firstName ? `for ${firstName} ` : ''}from your results
              {diagnosticAgo ? `, diagnostic taken ${diagnosticAgo}` : ''}. It re-orders as you
              learn: your weakest area always comes next.
            </>
          ) : (
            <>
              <Lock className="mr-1 inline h-3 w-3" aria-hidden />
              Your personalized path unlocks after the Initial Diagnostic. It maps all 8 areas
              and orders the course around what YOU need.
            </>
          )}
        </p>
      </div>

      <div className="relative" style={{ height: totalH }} data-testid="course-path-nodes">
        <svg
          className="absolute inset-0 h-full w-full"
          viewBox={`0 0 100 ${totalH}`}
          preserveAspectRatio="none"
          aria-hidden
        >
          <defs>
            <linearGradient id="sat-path-grad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="rgb(16 185 129)" />
              <stop offset="100%" stopColor="hsl(var(--primary))" />
            </linearGradient>
          </defs>
          {aheadRibbon && (
            <path
              d={aheadRibbon}
              fill="none"
              strokeWidth="2"
              strokeDasharray="5 5"
              vectorEffect="non-scaling-stroke"
              className="sat-ribbon-ahead stroke-border"
            />
          )}
          {solidRibbon && (
            <>
              <path
                d={solidRibbon}
                fill="none"
                strokeWidth="2.5"
                pathLength={1}
                vectorEffect="non-scaling-stroke"
                stroke="url(#sat-path-grad)"
                className="sat-ribbon-solid"
              />
              {/* the living current: energy pulses flowing along conquered ground */}
              <path
                d={solidRibbon}
                fill="none"
                strokeWidth="3.5"
                strokeLinecap="round"
                pathLength={100}
                strokeDasharray="1.5 8"
                vectorEffect="non-scaling-stroke"
                className="sat-ribbon-energy stroke-emerald-300/80 dark:stroke-emerald-400/60"
              />
            </>
          )}
        </svg>

        {path.map((node, i) => {
          const { entry, status } = node;
          const band = entry.masterySnapshot.overall.band;
          const score = entry.masterySnapshot.overall.scorePct;
          const isFocus = focusDomain === entry.domain;
          const done = status === 'DONE';
          const current = status === 'CURRENT';
          const inProgress =
            !done && !current && entry.workflowStatus !== 'NOT_STARTED' && entry.workflowStatus !== 'SKIPPED';

          return (
            <button
              key={entry.domain}
              type="button"
              aria-pressed={isFocus}
              onClick={() => onFocusDomain(isFocus ? null : entry.domain)}
              data-testid={`journey-domain-${entry.domain}`}
              className={cn(
                // -translate-x-1/2 is the resting transform; the pop keyframe animates to the
                // same value, so reduced-motion (animation: none) still positions correctly.
                'sat-node group absolute flex w-36 -translate-x-1/2 flex-col items-center gap-1 rounded-xl p-1 text-center',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
              )}
              style={{
                left: `${WAVE_X[i % WAVE_X.length]}%`,
                top: i * ROW_H + 4,
                animationDelay: `${0.15 + i * 0.09}s`,
              }}
            >
              {current && (
                <>
                  <Badge className="absolute -top-2.5 z-10 h-4 px-1.5 text-[9px] font-semibold uppercase tracking-wide shadow">
                    You are here
                  </Badge>
                  <span
                    aria-hidden
                    className="absolute top-[9px] h-14 w-14 animate-ping rounded-full bg-primary/10"
                    style={{ animationDuration: '2.4s' }}
                  />
                </>
              )}

              {done ? (
                <span
                  className={cn(
                    'flex h-16 w-16 items-center justify-center rounded-full bg-emerald-500 text-white shadow-md transition',
                    'group-hover:scale-105',
                    isFocus && 'ring-4 ring-emerald-500/30',
                  )}
                >
                  <Check className="h-7 w-7" aria-label="Mastered" />
                </span>
              ) : (
                <span
                  className={cn(
                    'rounded-full transition group-hover:scale-105',
                    current && 'drop-shadow-md',
                    isFocus && 'rounded-full ring-4 ring-primary/30',
                  )}
                >
                  <StopRing scorePct={personalized ? score : null} band={band}>
                    <span
                      className={cn(
                        'z-10 text-[13px] font-bold tracking-tight',
                        current ? 'text-foreground' : 'text-muted-foreground',
                      )}
                    >
                      {personalized && score != null ? (
                        `${Math.round(score)}%`
                      ) : (
                        <span
                          className={cn('block h-2.5 w-2.5 rounded-full', STUDENT_BAND_BAR_CLASSES[band])}
                          aria-hidden
                        />
                      )}
                    </span>
                  </StopRing>
                </span>
              )}

              <span className="max-w-full truncate text-xs font-semibold leading-tight">
                {SHORT_DOMAIN_LABELS[entry.domain]}
              </span>
              <span
                className={cn(
                  'text-[10px] leading-tight',
                  done ? 'font-medium text-emerald-600 dark:text-emerald-400' : 'text-muted-foreground',
                )}
              >
                {done
                  ? 'Mastered'
                  : !personalized
                    ? 'Awaiting diagnostic'
                    : inProgress
                      ? `In progress: ${STUDENT_BAND_LABELS[band]}`
                      : STUDENT_BAND_LABELS[band]}
              </span>
              <span className="sr-only">{DOMAIN_LABELS[entry.domain]}</span>
            </button>
          );
        })}
      </div>

      <p className="text-xs text-muted-foreground">Tap any stop to see its skills.</p>
    </section>
  );
}
